<template>
    <div class="DesignExamplesModals">
        <ComponentPlayground
            :component="TprmModal"
            :defaults="tprmModalDefaults"
            :description="tprmModalDesc"
            title="TprmModal"
        >
            <template #default="{ liveProps }">
                <div class="DesignExamplesModals__trigger">
                    <NewButton variant="fill" color="primary" @click="liveProps.open = true">
                        Open modal
                    </NewButton>
                </div>
                <TprmModal v-bind="liveProps" @close="liveProps.open = false">
                    <p class="DesignExamplesModals__bodyText">
                        This is the default slot content of the modal. Put any form, confirmation copy,
                        or interactive content here. The body uses a column flex layout with a 21px gap,
                        so siblings will stack with consistent spacing.
                    </p>
                    <template #footer>
                        <NewButton variant="flat" color="secondary" @click="liveProps.open = false">
                            Cancel
                        </NewButton>
                        <NewButton variant="fill" color="primary" @click="liveProps.open = false">
                            Confirm
                        </NewButton>
                    </template>
                </TprmModal>
            </template>
        </ComponentPlayground>

        <ComponentPlayground
            :component="ConfirmationModal"
            :defaults="confirmationModalDefaults"
            :description="confirmationModalDesc"
            title="ConfirmationModal"
        >
            <template #default="{ liveProps }">
                <div class="DesignExamplesModals__trigger">
                    <NewButton variant="fill" color="primary" @click="liveProps.open = true">
                        Open modal
                    </NewButton>
                </div>
                <ConfirmationModal
                    v-if="liveProps.open"
                    v-bind="liveProps"
                    @input="liveProps.open = false"
                    @close="liveProps.open = false"
                >
                    <p class="DesignExamplesModals__bodyText">
                        This is the body slot. Use it for any confirmation copy or details.
                        The footer renders Yes/No buttons by default; replace via the footer slot.
                    </p>
                </ConfirmationModal>
            </template>
        </ComponentPlayground>

        <ComponentPlayground
            :component="LoadingModal"
            :defaults="loadingModalDefaults"
            title="LoadingModal"
            description="Wrapper around TprmModal · Props: header, fullScreen"
        >
            <template #default="{ liveProps }">
                <div class="DesignExamplesModals__trigger">
                    <NewButton variant="fill" color="primary" @click="liveProps.open = true">
                        Open modal
                    </NewButton>
                </div>
                <LoadingModal
                    v-if="liveProps.open"
                    v-bind="liveProps"
                    @close="liveProps.open = false"
                />
            </template>
        </ComponentPlayground>
    </div>
</template>

<style lang="scss" scoped>
.DesignExamplesModals {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    &__trigger {
        display: flex;
        gap: 0.5rem;
        margin-bottom: 0.5rem;
    }

    &__bodyText {
        margin: 0;
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--eidTechGray);
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import LoadingModal from '@/components/loading-modal/LoadingModal.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import ComponentPlayground from '@/modules/design-examples/components/ComponentPlayground.vue';

    export default defineComponent({
        name: 'DesignExamplesModals',
        components: {
            ComponentPlayground,
            NewButton,
            TprmModal,
            ConfirmationModal,
            LoadingModal,
        },
        data() {
            return {
                TprmModal,
                ConfirmationModal,
                LoadingModal,
                tprmModalDesc: 'Sizes: default, md, lg, xl \xb7 Props: title, subtitle, size, showCloseIcon,' +
                    ' closeOnBackdrop, fullScreen \xb7 Slots: default (body), footer',
                confirmationModalDesc: 'Wrapper around TprmModal \xb7 Props: title, yes, no, yesColor,' +
                    ' noColor, yesVariant, noVariant, progress, allowClose',
                tprmModalDefaults: {
                    open: false,
                    title: 'Modal title',
                    subtitle: 'Optional subtitle describing the modal',
                    size: 'md',
                    showCloseIcon: true,
                    closeOnBackdrop: false,
                    fullScreen: false,
                },
                confirmationModalDefaults: {
                    open: false,
                    title: 'Are you sure?',
                    yes: 'Yes',
                    no: 'No',
                    yesColor: 'primary',
                    yesVariant: 'fill',
                    noColor: 'danger',
                    noVariant: 'outline',
                    progress: false,
                    allowClose: false,
                },
                loadingModalDefaults: {
                    open: false,
                    header: 'Loading…',
                    fullScreen: false,
                },
            };
        },
    });
</script>
